export const CourseQuizQuestionType = {
  MULTIPLE_CHOICE: 'multiple_choice',
  TRUE_FALSE: 'true_false',
} as const;

export type CourseQuizQuestionTypeValue =
  (typeof CourseQuizQuestionType)[keyof typeof CourseQuizQuestionType];

export const CourseQuizShowAnswersPolicy = {
  AFTER_PASS: 'after_pass',
  AFTER_FAIL: 'after_fail',
  AFTER_BOTH: 'after_both',
  NEVER: 'never',
} as const;

export type CourseQuizShowAnswersPolicyValue =
  (typeof CourseQuizShowAnswersPolicy)[keyof typeof CourseQuizShowAnswersPolicy];

export type CourseQuizChoiceDefinition = {
  id: string;
  label: string;
  isCorrect: boolean;
  sortOrder?: number;
};

export type CourseQuizQuestionDefinition = {
  id: string;
  prompt: string;
  type: CourseQuizQuestionTypeValue | string;
  sortOrder?: number;
  explanation?: string | null;
  choices: CourseQuizChoiceDefinition[];
};

export type CourseQuizAnswerMap = Record<string, string>;

export type CourseQuizQuestionResult = {
  questionId: string;
  correct: boolean;
  selectedChoiceId: string | null;
  correctChoiceId: string | null;
  explanation?: string | null;
};

export type CourseQuizGradeResult = {
  scorePercent: number;
  passed: boolean;
  correctCount: number;
  totalCount: number;
  questionResults: CourseQuizQuestionResult[];
};

export function normalizeQuizShowAnswersPolicy(
  policy?: string | null,
): CourseQuizShowAnswersPolicyValue {
  if (
    policy === CourseQuizShowAnswersPolicy.AFTER_PASS ||
    policy === CourseQuizShowAnswersPolicy.AFTER_FAIL ||
    policy === CourseQuizShowAnswersPolicy.AFTER_BOTH ||
    policy === CourseQuizShowAnswersPolicy.NEVER
  ) {
    return policy;
  }
  return CourseQuizShowAnswersPolicy.AFTER_BOTH;
}

export function canSubmitQuizAttempt(attemptCount: number, attemptLimit?: number | null): boolean {
  if (attemptLimit == null || attemptLimit <= 0) return true;
  return attemptCount < attemptLimit;
}

export function remainingQuizAttempts(attemptCount: number, attemptLimit?: number | null): number | null {
  if (attemptLimit == null || attemptLimit <= 0) return null;
  return Math.max(0, attemptLimit - attemptCount);
}

export function shouldRevealQuizAnswers(
  policy: CourseQuizShowAnswersPolicyValue | string,
  passed: boolean,
): boolean {
  const normalized = normalizeQuizShowAnswersPolicy(policy);
  if (normalized === CourseQuizShowAnswersPolicy.NEVER) return false;
  if (normalized === CourseQuizShowAnswersPolicy.AFTER_BOTH) return true;
  if (normalized === CourseQuizShowAnswersPolicy.AFTER_PASS) return passed;
  return !passed;
}

export function validateQuizQuestion(question: CourseQuizQuestionDefinition): string | null {
  const prompt = question.prompt?.trim();
  if (!prompt) return 'Each question needs a prompt';

  const type =
    question.type === CourseQuizQuestionType.TRUE_FALSE
      ? CourseQuizQuestionType.TRUE_FALSE
      : CourseQuizQuestionType.MULTIPLE_CHOICE;

  const choices = question.choices ?? [];
  const correctChoices = choices.filter((choice) => choice.isCorrect);

  if (type === CourseQuizQuestionType.TRUE_FALSE) {
    if (choices.length !== 2) return 'True/false questions need exactly two choices';
    if (correctChoices.length !== 1) return 'True/false questions need one correct answer';
    return null;
  }

  if (choices.length < 2) return 'Multiple-choice questions need at least two choices';
  if (correctChoices.length !== 1) return 'Multiple-choice questions need exactly one correct answer';
  return null;
}

export function validateQuizDefinition(questions: CourseQuizQuestionDefinition[]): string | null {
  if (!questions.length) return 'Add at least one question';
  for (const question of questions) {
    const error = validateQuizQuestion(question);
    if (error) return error;
  }
  return null;
}

export function gradeQuizAttempt(params: {
  questions: CourseQuizQuestionDefinition[];
  answers: CourseQuizAnswerMap;
  passScorePercent: number;
}): CourseQuizGradeResult {
  const { questions, answers, passScorePercent } = params;
  const questionResults: CourseQuizQuestionResult[] = [];
  let correctCount = 0;

  for (const question of questions) {
    const selectedChoiceId = answers[question.id] ?? null;
    const correctChoice = question.choices.find((choice) => choice.isCorrect) ?? null;
    const correct = Boolean(selectedChoiceId && correctChoice && selectedChoiceId === correctChoice.id);
    if (correct) correctCount += 1;

    questionResults.push({
      questionId: question.id,
      correct,
      selectedChoiceId,
      correctChoiceId: correctChoice?.id ?? null,
      explanation: question.explanation ?? null,
    });
  }

  const totalCount = questions.length;
  const scorePercent =
    totalCount === 0 ? 0 : Math.round((correctCount / totalCount) * 100);
  const passed = scorePercent >= Math.max(0, Math.min(100, passScorePercent));

  return {
    scorePercent,
    passed,
    correctCount,
    totalCount,
    questionResults,
  };
}

export type CourseQuizLearnerQuestion = {
  id: string;
  prompt: string;
  type: CourseQuizQuestionTypeValue;
  sortOrder: number;
  choices: Array<{ id: string; label: string; sortOrder: number }>;
};

export function stripQuizAnswersForLearner(
  questions: CourseQuizQuestionDefinition[],
): CourseQuizLearnerQuestion[] {
  return questions
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((question) => ({
      id: question.id,
      prompt: question.prompt,
      type:
        question.type === CourseQuizQuestionType.TRUE_FALSE
          ? CourseQuizQuestionType.TRUE_FALSE
          : CourseQuizQuestionType.MULTIPLE_CHOICE,
      sortOrder: question.sortOrder ?? 0,
      choices: question.choices
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((choice) => ({
          id: choice.id,
          label: choice.label,
          sortOrder: choice.sortOrder ?? 0,
        })),
    }));
}

export function buildDefaultTrueFalseChoices(): Array<Omit<CourseQuizChoiceDefinition, 'id'>> {
  return [
    { label: 'True', isCorrect: true, sortOrder: 0 },
    { label: 'False', isCorrect: false, sortOrder: 1 },
  ];
}
